import { Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import type { DisputeReason } from "@/types/dispute";

import { useCreateDispute, useOrderDisputeReasons } from "@/api/dispute-query";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Textarea } from "../ui/textarea";

type RejectOrderStep = "confirm" | "issues" | "questions" | "description";

export function RejectOrder({ orderId, onSuccess }: { orderId: string; onSuccess: () => void }) {
	const [step, setStep] = useState<RejectOrderStep>("confirm");
	const [issue, setIssue] = useState<DisputeReason | null>(null);
	const [additionalReason, setAdditionalReason] = useState("");
	const [selectedQuestion, setSelectedQuestion] = useState<string>("");

	const reasonsQuery = useOrderDisputeReasons();
	const createDispute = useCreateDispute();

	const handleQuestionChange = (id: string) => {
		setSelectedQuestion(id);
	};

	const handleContinue = () => {
		setStep("issues");
	};

	const handleSubmit = async () => {
		if (!issue)
			throw new Error("No issue selected");
		const questionId = selectedQuestion;
		if (!questionId)
			throw new Error("No question selected");

		createDispute.mutateAsync({ orderId, questionId, additionalReason })
			.then(() => {
				toast.success("Order rejected successfully");
				onSuccess();
			})
			.catch(() => {
				toast.error("Failed to reject order");
			});
	};

	return (
		<Dialog onOpenChange={isOpen => !isOpen && setStep("confirm")}>
			<DialogTrigger asChild>
				<Button variant="accent">Reject this order</Button>
			</DialogTrigger>
			<DialogTitle className="sr-only">Reject Order</DialogTitle>
			<DialogDescription className="sr-only">Reject an order from a a vendor</DialogDescription>
			{
				step === "confirm" && (
					<DialogContent className="gap-1 flex-row">
						<div className="grid place-content-center gap-4 py-4">
							<div className="size-20 bg-hover-secondary rounded-full p-2 grid place-content-center text-white">
								<div className="size-16 bg-primary rounded-full p-2 grid place-content-center text-white text-2xl">
									!
								</div>
							</div>
						</div>
						<div>
							<p className="font-bold text-center text-xl">Confirm Order</p>
							<p className="text-center">
								Are you sure you want to you want to reject this order ?
							</p>
						</div>
						<DialogFooter className="mt-6">
							<Button className="w-full" variant="secondary">No, I want to keep this order</Button>
							<Button
								className="w-full"
								onClick={handleContinue}
							>
								Yes, I want to reject this order
							</Button>
						</DialogFooter>
					</DialogContent>
				)
			}
			{
				step === "issues" && (
					<DialogContent className="max-w-md p-6">
						<DialogHeader>
							<DialogTitle className="text-xl font-semibold">Why do you want to reject this order?</DialogTitle>
						</DialogHeader>

						<p className="text-gray-600 mb-6 text-xs">Tell us why you want to reject this order, this will help us serve you better next time. Your money will be refunded within 24hrs</p>
						<div>
							{reasonsQuery?.data && reasonsQuery?.data.map(reason => (
								<Button
									variant="ghost"
									key={reason.id}
									className="flex w-full px-0 items-start justify-start space-x-2"
									onClick={() => {
										setIssue(reason);
										setStep("questions");
									}}
								>
									<p
										className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
									>
										{reason.name}
									</p>
								</Button>
							))}
						</div>
					</DialogContent>
				)
			}
			{
				step === "questions" && issue && (
					<IssueQuestions
						issueTitle={issue.name}
						issueDescription=""
						questions={issue.questions}
						selectedQuestion={selectedQuestion}
						handleCancel={() => {
							setSelectedQuestion("");
							setStep("issues");
						}}
						isPending={createDispute.isPending}
						handleSubmit={() => setStep("description")}
						handleQuestionChange={handleQuestionChange}
					/>
				)
			}
			{
				step === "description" && (
					<DialogContent className="max-w-md p-6">
						<DialogHeader>
							<DialogTitle className="text-xl font-semibold">Why do you want to reject this order?</DialogTitle>
						</DialogHeader>

						<p className="text-gray-600 mb-6 text-xs">Tell us why you want to reject this order, this will help us serve you better next time. Your money will be refunded within 24hrs</p>
						<Textarea
							rows={4}
							className="w-full p-2 border rounded resize-none"
							onChange={e => setAdditionalReason(e.target.value.trim())}
							placeholder="Please provide more details..."
						/>

						<DialogFooter>
							<DialogTrigger asChild>
								<Button
									onClick={handleSubmit}
									disabled={createDispute.isPending}
								>
									{createDispute.isPending
										? (
												<>
													<Loader2 size={20} className="animate-spin" />
													{" "}
												&nbsp; Loading....
												</>
											)
										: (
												"Submit"
											)}
								</Button>
							</DialogTrigger>
						</DialogFooter>
					</DialogContent>

				)
			}
		</Dialog>
	);
}

interface Question {
	createdAt: string;
	updatedAt: string;
	deletedAt: string | null;
	id: string;
	text: string;
	__type: string;
}
interface IssueQuestionsProps {
	issueTitle: string;
	isPending: boolean;
	issueDescription: string;
	questions: Question[];
	selectedQuestion: string;
	handleCancel: () => void;
	handleSubmit: () => void;
	handleQuestionChange: (id: string) => void;
}

function IssueQuestions({ issueTitle, selectedQuestion, issueDescription, isPending, questions, handleCancel, handleSubmit, handleQuestionChange }: IssueQuestionsProps) {
	return (
		<DialogContent className="max-w-md p-6">
			<DialogHeader>
				<DialogTitle className="text-xl font-semibold">{issueTitle}</DialogTitle>
			</DialogHeader>

			<p className="text-gray-600 mb-6">{issueDescription}</p>

			<RadioGroup className="space-y-4" onValueChange={handleQuestionChange}>
				{questions.map(question => (
					<div key={question.id} className="flex items-center space-x-2">
						<RadioGroupItem
							id={question.id}
							value={question.id}
						/>
						<Label
							htmlFor={question.id}
							className="cursor-pointer text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
						>
							{question.text}
						</Label>
					</div>
				))}
			</RadioGroup>

			<div className="flex justify-between mt-8">
				<Button variant="outline" onClick={handleCancel}>
					Go back
				</Button>
				<Button
					onClick={handleSubmit}
					disabled={!selectedQuestion || isPending}
				>
					{isPending
						? (
								<>
									<Loader2 size={20} className="animate-spin" />
									{" "}
									&nbsp; Loading....
								</>
							)
						: (
								"Continue"
							)}
				</Button>
			</div>
		</DialogContent>
	);
}
